import { Controller } from "@hotwired/stimulus"
import Honeybadger from "@honeybadger-io/js"

// Handles drop zones for resources in Plantafel task rows
export default class extends Controller {
  static values = {
    taskId: Number,
    date: String,
  }

  connect() {
    this.element.addEventListener("dragover", this.handleDragOver.bind(this))
    this.element.addEventListener("dragenter", this.handleDragEnter.bind(this))
    this.element.addEventListener("dragleave", this.handleDragLeave.bind(this))
    this.element.addEventListener("drop", this.handleDrop.bind(this))
  }

  disconnect() {
    this.element.removeEventListener("dragover", this.handleDragOver.bind(this))
    this.element.removeEventListener("dragenter", this.handleDragEnter.bind(this))
    this.element.removeEventListener("dragleave", this.handleDragLeave.bind(this))
    this.element.removeEventListener("drop", this.handleDrop.bind(this))
  }

  handleDragOver(event) {
    // Ignore if task dragging is in progress (this controller handles resource drops only)
    if (document.body.dataset.plantafelTaskDragging) {
      return
    }
    event.preventDefault()
    event.dataTransfer.dropEffect = "copy"
  }

  handleDragEnter(event) {
    // Ignore if task dragging is in progress
    if (document.body.dataset.plantafelTaskDragging) {
      return
    }
    event.preventDefault()
    this.element.classList.add("bg-teal-100", "ring-2", "ring-teal-500")
  }

  handleDragLeave(event) {
    // Ignore if task dragging is in progress
    if (document.body.dataset.plantafelTaskDragging) {
      return
    }
    // Only remove highlight if we're actually leaving the element
    if (!this.element.contains(event.relatedTarget)) {
      this.element.classList.remove("bg-teal-100", "ring-2", "ring-teal-500")
    }
  }

  handleDrop(event) {
    event.preventDefault()
    this.element.classList.remove("bg-teal-100", "ring-2", "ring-teal-500")

    // Ignore if task dragging is in progress
    if (document.body.dataset.plantafelTaskDragging) {
      return
    }

    try {
      const jsonString = event.dataTransfer.getData("application/json")

      // Guard against empty or missing data (can happen with interrupted drags)
      if (!jsonString) {
        return
      }

      const data = JSON.parse(jsonString)

      // Ignore task move drags (only handle resource drops)
      if (data.dragType === "task-move") {
        return
      }
      const { resourceId, resourceType, resourceName } = data

      // Get task and date from element data attributes
      const taskId = this.element.dataset.taskId || this.taskIdValue
      const date = this.element.dataset.date || this.dateValue
      // The bar this drop zone sits in. A task worked in several periods draws one bar
      // each, and dropping on one of them assigns for that period alone; the server
      // ignores it for single-period tasks, where bar and task are the same thing.
      const timespanId = this.element.closest("[data-timespan-id]")?.dataset.timespanId

      if (!taskId || !resourceId || !resourceType) {
        Honeybadger.notify("Missing required data for resource assignment", {
          context: { level: "warning", component: "plantafel-drop" },
        })
        return
      }

      // Dispatch custom event for main controller to handle
      const dropEvent = new CustomEvent("plantafel:resource-dropped", {
        bubbles: true,
        detail: {
          taskId,
          date,
          timespanId,
          resourceId,
          resourceType,
          resourceName,
        },
      })

      this.element.dispatchEvent(dropEvent)

      // Show immediate visual feedback
      this.showDropFeedback(resourceName)
    } catch (error) {
      Honeybadger.notify(error, { context: { component: "plantafel-drop", action: "handle_drop" } })
    }
  }

  showDropFeedback(_resourceName) {
    // Create temporary indicator - small text overlay without changing element size
    const indicator = document.createElement("span")
    indicator.className = "ml-2 text-xs text-gray-600 font-medium animate-pulse"
    indicator.textContent = "Loading..."

    // Find the employees container and append loading text
    const employeesContainer = this.element.querySelector('[id$="_employees"]')
    if (employeesContainer) {
      employeesContainer.appendChild(indicator)
    }

    // Remove after animation or when Turbo updates the DOM
    setTimeout(() => indicator.remove(), 2000)
  }
}
